import {getFormProps, getInputProps, useForm} from '@conform-to/react'
import {parseWithZod} from '@conform-to/zod'
import type {ActionFunctionArgs} from '@remix-run/node'
import {json} from '@remix-run/node'
import {Form, Link, useActionData, useNavigation} from '@remix-run/react'
import {Button, TextField} from '@translated/babele-react'
import {HTTPError} from 'got'
import {useId} from 'react'
import {z} from 'zod'

import {resetPassword} from '~/models/auth/auth.server'

const schema = z.object({
  email: z
    .string({required_error: 'Email is required'})
    .email('Email is invalid'),
})

export const action = async ({request}: ActionFunctionArgs) => {
  const formData = await request.formData()

  const submission = await parseWithZod(formData, {
    async: true,
    schema: schema.transform(async ({email}, ctx) => {
      try {
        return await resetPassword(email)
      } catch (error) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message:
            error instanceof HTTPError && error.response.statusCode == 400
              ? 'Invalid username'
              : 'Unexpected error',
        })
        return z.NEVER
      }
    }),
  })

  return json(submission.reply())
}

export default function ForgotPassword() {
  const id = useId()
  const actionData = useActionData<typeof action>()
  const navigation = useNavigation()
  const [form, {email}] = useForm({
    shouldRevalidate: 'onInput',
    onValidate({formData}) {
      return parseWithZod(formData, {schema})
    },
    id,
    lastResult: actionData,
  })

  const isSubmitSuccess = actionData?.status == 'success'
  return (
    <div className="grid place-items-center">
      <div className="max-w-sm w-full flex flex-col gap-12">
        <h1 className="text-display-md font-bold">Reset password</h1>
        {!isSubmitSuccess ? (
          <Form method="post" {...getFormProps(form)}>
            <div className="flex flex-col gap-8">
              {form.errors && (
                <div className="bg-critical-100 p-4">{form.errors}</div>
              )}
              <div className="flex flex-col gap-2">
                <label htmlFor={email.id} className="font-medium">
                  Email address
                </label>
                <TextField
                  {...getInputProps(email, {
                    type: 'email',
                  })}
                  autoFocus
                  placeholder="you@yourcompany.com"
                  type="email"
                />
                {email.errors && (
                  <div className="text-critical-800 text-label-md">
                    {email.errors}
                  </div>
                )}
              </div>

              <Button
                variant={{mode: 'solid', tone: 'primary'}}
                type="submit"
                isLoading={navigation.state != 'idle'}
              >
                Send reset instructions
              </Button>
            </div>
          </Form>
        ) : (
          <div className="flex flex-col gap-8">
            <div className="p-4 bg-yellow-100 rounded-md">
              You will receive an email with instructions about how to reset
              your password in a few minutes
            </div>
            <Button
              variant={{tone: 'primary', mode: 'outline'}}
              render={<Link to="/login">Back to sign in</Link>}
            />
          </div>
        )}
      </div>
    </div>
  )
}
